import { useMemo, useCallback } from 'react';
import { isDescendantOf } from '@/lib/outline/outline-tree-utils';

export interface PositionedNode {
    node: OutlineNode;
    depth: number;
    topPercent: number;
    heightPercent: number;
    absTopPercent: number;
    absHeightPercent: number;
    absBottomPercent: number; // absTopPercent + absHeightPercent
    absMidPercent: number; // 0..1 midpoint
    isHighlighted: boolean;
    parentId?: string;
    siblingIndex?: number;
}

function collectLeavesInOrder(node: OutlineNode): { node: OutlineNode; numVerses: number }[] {
    if (!node.children || node.children.length === 0) {
        return [{ node, numVerses: node.numVerses || 0 }];
    }
    return node.children.flatMap(child => collectLeavesInOrder(child));
}

export function useIciclePositioning(outline: Outline, focusedNode?: OutlineNode, highlightedNode?: OutlineNode) {
    const roots = outline.structure;

    const { leaves, totalVerses } = useMemo(() => {
        const allLeaves = roots.flatMap(r => collectLeavesInOrder(r));
        const total = outline.numVerses || allLeaves.reduce((s, l) => s + (l.numVerses || 0), 0);
        return { leaves: allLeaves, totalVerses: total };
    }, [roots, outline.numVerses]);

    const leafOffsets = useMemo(() => {
        const offsets: number[] = [];
        let acc = 0;
        for (const l of leaves) {
            offsets.push(acc);
            acc += (l.numVerses || 0);
        }
        return offsets;
    }, [leaves]);

    const nodeSpanPercent = useCallback((node: OutlineNode): { topPercent: number; heightPercent: number } => {
        let first = -1;
        let last = -1;
        for (let i = 0; i < leaves.length; i++) {
            if (isDescendantOf(leaves[i].node, node)) { first = i; break; }
        }
        for (let i = leaves.length - 1; i >= 0; i--) {
            if (isDescendantOf(leaves[i].node, node)) { last = i; break; }
        }
        if (first === -1 || last === -1) return { topPercent: 0, heightPercent: 0 };
        const startVerses = leafOffsets[first];
        const endVerses = leafOffsets[last] + (leaves[last].numVerses || 0);
        const topPercent = totalVerses > 0 ? (startVerses / totalVerses) * 100 : 0;
        const heightPercent = totalVerses > 0 ? ((endVerses - startVerses) / totalVerses) * 100 : 0;
        return { topPercent, heightPercent };
    }, [leaves, leafOffsets, totalVerses]);

    const positioned = useMemo<PositionedNode[]>(() => {
        const nodes: PositionedNode[] = [];
        const rootDepth = 0;
        const visit = (node: OutlineNode, depth: number, parentId?: string, siblingIndex?: number) => {
            const { topPercent, heightPercent } = nodeSpanPercent(node);
            const isHighlighted = highlightedNode ? isDescendantOf(node, highlightedNode) : false;
            const absBottomPercent = topPercent + heightPercent;
            const absMidPercent = (topPercent + absBottomPercent) / 200;
            nodes.push({ node, depth, topPercent, heightPercent, absTopPercent: topPercent, absHeightPercent: heightPercent, absBottomPercent, absMidPercent, isHighlighted, parentId, siblingIndex });
            if (node.children) {
                for (let i = 0; i < node.children.length; i++) {
                    const c = node.children[i]!;
                    visit(c, depth + 1, node.id, i);
                }
            }
        };
        for (let i = 0; i < roots.length; i++) { visit(roots[i]!, rootDepth, undefined, i); }
        return nodes;
    }, [roots, highlightedNode, nodeSpanPercent]);

    const filtered = useMemo<PositionedNode[]>(() => {
        if (!focusedNode) return positioned;
        const focusedNodePosition = positioned.find(p => p.node.id === focusedNode.id);
        const baseDepth = focusedNodePosition?.depth ?? 0;
        const maxDepth = baseDepth + 2;

        let filteredNodes = positioned.filter(p => isDescendantOf(p.node, focusedNode) && p.depth <= maxDepth);

        if (highlightedNode) {
            const highlightedDepth = positioned.find(p => p.node.id === highlightedNode.id)?.depth ?? 0;
            if (highlightedDepth === baseDepth + 1) {
                filteredNodes = filteredNodes.filter(p => {
                    if (p.depth === baseDepth) return true;
                    if (p.depth === baseDepth + 1) return true;
                    if (p.depth === baseDepth + 2) return p.parentId === highlightedNode.id;
                    return false;
                });
            }
        }
        // Recompute heights relative to immediate parent
        const recalculated = filteredNodes.map(p => {
            if (focusedNode && p.node.id === focusedNode.id) {
                return { ...p, heightPercent: 100 };
            }
            const parentNode = p.parentId ? positioned.find(pos => pos.node.id === p.parentId)?.node : null;
            if (!parentNode) return p;
            const parentVerses = parentNode.numVerses || 0;
            const nodeVerses = p.node.numVerses || 0;
            const relativeHeightPercent = parentVerses > 0 ? (nodeVerses / parentVerses) * 100 : 0;
            return { ...p, heightPercent: relativeHeightPercent };
        });
        return recalculated;
    }, [positioned, focusedNode, highlightedNode]);

    const baseDepth = useMemo(() => {
        if (!focusedNode) return 0;
        const found = positioned.find(p => p.node.id === focusedNode.id);
        return found ? found.depth : -1;
    }, [focusedNode, positioned]);

    const nodesByColumn = useMemo(() => {
        const columns: Record<number, PositionedNode[]> = {};
        for (const node of filtered) {
            const col = (node.depth - baseDepth);
            if (col <= 1) {
                if (!columns[col]) columns[col] = [];
                columns[col].push(node);
            }
        }
        return columns;
    }, [filtered, baseDepth]);

    const column1Nodes = nodesByColumn[1] || [];

    return { nodesByColumn, column1Nodes, totalVerses };
}



